/**
 * @fileoverview Account-apps-ui strings barrel — the EN/ES bundles plus
 *   `loadAccountAppsUiStrings(i18nInstance, locales)`, which registers them under the `accountAppsUi`
 *   namespace on a react-i18next instance.
 * @package @puffin-core/account-apps-ui
 *
 * ## 🔴 LEAF-ONLY by design — this subpath must not import a component
 *
 * `@puffin-core/i18n-namespaces` imports this module at APP BOOT from every web app, so anything
 * reachable from here lands in every boot chunk. It imports only its own frozen data modules and a
 * zero-import namespace constant.
 *
 * @module @puffin-core/account-apps-ui/strings
 */

import { ACCOUNT_APPS_UI_EN } from './en.js';
import { ACCOUNT_APPS_UI_ES } from './es.js';
import { ACCOUNT_APPS_UI_NAMESPACE } from './namespace.js';

export { ACCOUNT_APPS_UI_EN, ACCOUNT_APPS_UI_ES, ACCOUNT_APPS_UI_NAMESPACE };

const BUNDLES = Object.freeze({ en: ACCOUNT_APPS_UI_EN, es: ACCOUNT_APPS_UI_ES });

/**
 * The locales this package ships bundles for — DERIVED from `BUNDLES`, never typed out (a restated
 * list is a second copy of a fact, ENUM-DRIFT-001).
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more account-apps-ui bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a locale is unsupported.
 */
export function loadAccountAppsUiStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadAccountAppsUiStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadAccountAppsUiStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, ACCOUNT_APPS_UI_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
